'use strict';

var { findWhere } = require('underscore'),
    AppDispatcher = require('../AppDispatcher'),
    ActionTypes = require('../constants/ActionTypes'),
    MediaTypes = require('../constants/MediaTypes'),
    ScreenSizeStore = require('../stores/ScreenSizeStore'),
    update = require('react/lib/update'),
    keyMirror = require('react/lib/keyMirror'),
    { createStore } = require('../utils/StoreUtils');

var PlaybackStates = keyMirror({
  IDLE: null,
  PLAYING: null,
  PAUSED: null
});

var BACKWARD_THRESHOLD_SECONDS = 3,
    DEFAULT_STATE = { playbackState: PlaybackStates.IDLE };

var _mediaStates = {},
    _pendingUnregister = {},
    _stickyMediaId = null;

function isStickyPlayerVisible() {
  return !ScreenSizeStore.isMobile();
}

function isAudio(mediaId) {
  switch (_mediaStates[mediaId].mediaType) {
  case MediaTypes.AUDIO_TRACK:
  case MediaTypes.AUDIO_PLAYLIST:
    return true;
  default:
    return false;
  }
}

function findMediaStateWhere(properties) {
  return findWhere(_mediaStates, properties) || DEFAULT_STATE;
}

function isPlaying(mediaId) {
  return findMediaStateWhere({ id: mediaId }).playbackState === PlaybackStates.PLAYING;
}

function isPaused(mediaId) {
  return findMediaStateWhere({ id: mediaId }).playbackState === PlaybackStates.PAUSED;
}

function isIdle(mediaId) {
  return findMediaStateWhere({ id: mediaId }).playbackState === PlaybackStates.IDLE;
}

function hasNextTrack(mediaId) {
  var { tracks, trackIndex } = _mediaStates[mediaId];
  return !!tracks[trackIndex + 1];
}

function hasPreviousTrack(mediaId) {
  var { tracks, trackIndex } = _mediaStates[mediaId];
  return !!tracks[trackIndex - 1];
}

function getCurrentTime(mediaId) {
  return !isIdle(mediaId) && _mediaStates[mediaId].currentTime || 0;
}

function createMediaState(mediaId) {
  _mediaStates[mediaId] = {
    id: mediaId,
    tracks: [],
    trackIndex: 0,
    playbackState: PlaybackStates.IDLE
  };
}

function updateMediaState(mediaId, updateSpec) {
  if (!_mediaStates[mediaId]) {
    createMediaState(mediaId);
  }

  _mediaStates[mediaId] = update(_mediaStates[mediaId], updateSpec);
}

function setPlaybackState(mediaId, playbackState) {
  updateMediaState(mediaId, {
    playbackState: {
      $set: playbackState
    }
  });
}

function registerAudio(mediaId, options) {
  delete _pendingUnregister[mediaId];
  updateMediaState(mediaId, { $merge: options });
}

function canUnregisterAudio(mediaId) {
  return !isStickyPlayerVisible() || mediaId !== _stickyMediaId;
}

function unregisterAudio(mediaId, force) {
  if (canUnregisterAudio(mediaId) || force) {
    delete _mediaStates[mediaId];
    delete _pendingUnregister[mediaId];
  } else {
    _pendingUnregister[mediaId] = true;
  }
}

function considerSetStickyMedia(mediaId) {
  if (_mediaStates[mediaId] && !_stickyMediaId) {
    _stickyMediaId = mediaId;
  }
}

function considerRemoveStickyMedia(force) {
  if (!_stickyMediaId) {
    return;
  }

  var shouldRemove = force || !isStickyPlayerVisible() || isIdle(_stickyMediaId);
  if (!shouldRemove) {
    return;
  }

  if (_pendingUnregister[_stickyMediaId]) {
    unregisterAudio(_stickyMediaId, true);
  }

  _stickyMediaId = null;
}

function play(mediaId, options) {
  if (options) {
    updateMediaState(mediaId, { $merge: options });
  }

  var playingId = findMediaStateWhere({ playbackState: PlaybackStates.PLAYING }).id;
  if (playingId !== mediaId) {
    setPlaybackState(playingId, PlaybackStates.PAUSED);
  }

  setPlaybackState(mediaId, PlaybackStates.PLAYING);

  if (isAudio(mediaId)) {
    _stickyMediaId = mediaId;
  }
}

function pause(mediaId) {
  setPlaybackState(mediaId, PlaybackStates.PAUSED);
}

function playNext(mediaId) {
  if (!hasNextTrack(mediaId)) {
    setPlaybackState(mediaId, PlaybackStates.IDLE);
  }

  updateMediaState(mediaId, {
    currentTime: {
      $set: 0
    },
    trackIndex: {
      $apply: i => hasNextTrack(mediaId) ? i + 1 : 0
    }
  });
}

function playPrevious(mediaId) {
  var hasPrevious = hasPreviousTrack(mediaId),
      shouldPlayPrevious = hasPrevious && getCurrentTime(mediaId) < BACKWARD_THRESHOLD_SECONDS;

  updateMediaState(mediaId, {
    currentTime: {
      $set: 0
    },
    trackIndex: {
      $apply: i => shouldPlayPrevious ? i - 1 : i
    }
  });
}

function setCurrentTime(mediaId, time) {
  updateMediaState(mediaId, {
    currentTime: {
      $set: time
    }
  });
}

var PlayerStore = createStore({
  isPlaying: isPlaying,

  isPaused: isPaused,

  isIdle: isIdle,

  getCurrentTime: getCurrentTime,

  getStickyMediaId() {
    return _stickyMediaId;
  },

  getActiveTrackIndex(mediaId) {
    return _mediaStates[mediaId] && _mediaStates[mediaId].trackIndex || 0;
  },

  getActiveTrack(mediaId) {
    return _mediaStates[mediaId] && _mediaStates[mediaId].tracks[_mediaStates[mediaId].trackIndex];
  },

  getSourceStampId(mediaId) {
    return _mediaStates[mediaId] && _mediaStates[mediaId].stampId;
  },

  getArtwork(mediaId) {
    var mediaState = _mediaStates[mediaId];
    if (!mediaState) {
      return;
    }

    var track = mediaState.tracks[mediaState.trackIndex];
    return track && track.artwork || this.getBackground(mediaId);
  },

  getBackground(mediaId) {
    var mediaState = _mediaStates[mediaId];
    return mediaState && mediaState.background;
  }
});

AppDispatcher.register(function (payload) {
  var action = payload.action;

  switch (action.type) {
  case ActionTypes.PLAYER_PLAY:
    play(action.mediaId, action.options);
    PlayerStore.emitChange();
    break;

  case ActionTypes.PLAYER_PAUSE:
    pause(action.mediaId);
    PlayerStore.emitChange();
    break;

  case ActionTypes.PLAYER_PLAY_NEXT:
    playNext(action.mediaId);
    PlayerStore.emitChange();
    break;

  case ActionTypes.PLAYER_PLAY_PREVIOUS:
    playPrevious(action.mediaId);
    PlayerStore.emitChange();
    break;

  case ActionTypes.PLAYER_SET_CURRENT_TIME:
    setCurrentTime(action.mediaId, action.time);
    PlayerStore.emitChange();
    break;

  case ActionTypes.PLAYER_CONSIDER_SET_STICKY_MEDIA:
    considerSetStickyMedia(action.mediaId);
    PlayerStore.emitChange();
    break;

  case ActionTypes.PLAYER_CONSIDER_REMOVE_STICKY_MEDIA:
    considerRemoveStickyMedia(action.force);
    PlayerStore.emitChange();
    break;

  case ActionTypes.PLAYER_REGISTER_AUDIO:
    registerAudio(action.mediaId, action.options);
    PlayerStore.emitChange();
    break;

  case ActionTypes.PLAYER_UNREGISTER_AUDIO:
    unregisterAudio(action.mediaId);
    PlayerStore.emitChange();
    break;
  }
});

module.exports = PlayerStore;


/** WEBPACK FOOTER **
 ** ./stores/PlayerStore.js
 **/